import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { SupportLayout } from "@/components/support-layout";

const GOLD = "#C9A867";

const positions = [
  {
    title: "Social Media / Community Manager",
    location: "Remote",
    type: "Full-time",
    description:
      "Lead our social media strategy and build an engaged community around Nordex. You'll be the voice of our DEX across Twitter, Discord, Telegram, and other platforms.",
    responsibilities: [
      "Develop and execute social media content strategy across all platforms",
      "Build and nurture an active community of traders and DeFi enthusiasts",
      "Create engaging content including announcements, educational posts, and market updates",
      "Monitor and respond to community feedback, questions, and concerns",
      "Collaborate with marketing and product teams on campaigns and launches",
      "Track analytics and optimize engagement strategies",
      "Manage Discord/Telegram communities and moderate discussions",
    ],
    requirements: [
      "3+ years experience in social media management or community building",
      "Deep understanding of DeFi, DEXs, and cryptocurrency markets",
      "Proven track record of growing engaged online communities",
      "Excellent written communication skills",
      "Experience with Twitter/X, Discord, Telegram, and Reddit",
      "Ability to explain complex technical concepts simply",
      "Native or fluent English",
    ],
  },
  {
    title: "Customer Support Specialist (Scandinavia)",
    location: "Remote (Scandinavia)",
    type: "Full-time",
    description:
      "Help our Scandinavian users navigate the Nordex platform and resolve issues. You'll be the first point of contact for customers in the Nordic region.",
    responsibilities: [
      "Provide exceptional customer support via email, chat, and social media",
      "Help users with onboarding, account issues, and trading questions",
      "Troubleshoot technical issues and escalate when necessary",
      "Create and maintain help documentation in Scandinavian languages",
      "Gather customer feedback and communicate it to the product team",
      "Monitor support channels and maintain fast response times",
      "Educate users about DeFi, trading, and platform features",
    ],
    requirements: [
      "Native speaker of Swedish, Norwegian, or Danish",
      "Fluent in English (written and spoken)",
      "2+ years experience in customer support or success",
      "Strong problem-solving and communication skills",
      "Interest in cryptocurrency and DeFi (experience is a plus)",
      "Patient, empathetic, and detail-oriented",
      "Available to work flexible hours including some weekends",
      "Located in Scandinavia (Sweden, Norway, Denmark, or Finland)",
    ],
  },
];

export const Careers = () => {
  return (
    <SupportLayout maxWidth="max-w-5xl">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <h1 className="text-3xl font-bold mb-3 tracking-tight">
          Join the Nordex Team
        </h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-xl">
          Help us build the future of decentralized trading. We're looking for
          passionate, talented individuals to join our growing team.
        </p>
      </motion.div>

      {/* Why join us */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-12 p-6 border border-border rounded-lg bg-card"
      >
        <h2 className="text-xl font-semibold mb-6">Why Nordex?</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { title: "Remote First", description: "Work from anywhere in the world" },
            { title: "Competitive Pay", description: "Market-rate salaries and equity options" },
            { title: "Flexible Hours", description: "Work when you're most productive" },
            { title: "Cutting Edge", description: "Work with latest DeFi technology" },
            { title: "Impact", description: "Your work directly shapes the product" },
            { title: "Growth", description: "Learn and grow with the team" },
          ].map((item, index) => (
            <div key={index}>
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: GOLD }}
                />
                <h3 className="font-semibold text-sm">{item.title}</h3>
              </div>
              <p className="text-xs text-muted-foreground pl-4">{item.description}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Open positions */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-semibold mb-6">Open Positions</h2>
        <div className="space-y-6">
          {positions.map((position, index) => (
            <motion.div
              key={position.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="p-5 border border-border rounded-lg bg-card"
            >
              {/* Header */}
              <div className="mb-4">
                <h3 className="font-semibold mb-1">{position.title}</h3>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {position.location}
                  </span>
                  <span className="px-2 py-0.5 bg-accent border border-border rounded text-xs">
                    {position.type}
                  </span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                {position.description}
              </p>

              {/* Responsibilities */}
              <div className="mb-4">
                <h4 className="font-semibold text-sm mb-2">Responsibilities</h4>
                <ul className="space-y-1.5">
                  {position.responsibilities.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <ArrowRight
                        className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"
                        style={{ color: GOLD }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div className="mb-4">
                <h4 className="font-semibold text-sm mb-2">Requirements</h4>
                <ul className="space-y-1.5">
                  {position.requirements.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <ArrowRight
                        className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"
                        style={{ color: GOLD }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105"
                style={{ background: GOLD, color: "white" }}
              >
                Apply for this position
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Contact */}
      <div className="mb-12 p-6 border border-border rounded-lg bg-card text-center">
        <h3 className="font-semibold mb-2">Don't see a position that fits?</h3>
        <p className="text-sm text-muted-foreground mb-4">
          We're always looking for talented people. Send us your resume and tell
          us what you'd like to work on.
        </p>
        <a
          href="/contact"
          className="inline-flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-80"
          style={{ color: GOLD }}
        >
          Contact Us
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>

    </SupportLayout>
  );
};
